import { validateEmail, validateRequired, validateMaxLength } from './validation.js'

export class BaseForm {
  constructor(formElement) {
    this.form = formElement
    this.isSubmitting = false
    this.setupEventListeners()
  }
  
  setupEventListeners() {
    if (this.form) {
      this.form.addEventListener('submit', this.handleSubmit.bind(this))
    }
  }
  
  getFormData() {
    const formData = new FormData(this.form)
    const data = {}
    
    for (const [key, value] of formData.entries()) {
      data[key] = value
    }
    
    return data
  }
  
  async handleSubmit(event) {
    event.preventDefault()
    
    if (this.isSubmitting) {
      return
    }
    
    const formData = this.getFormData()
    const validation = this.validate(formData)
    
    if (!validation.isValid) {
      this.displayErrors(validation.errors)
      return
    }
    
    this.clearErrors()
    this.setLoadingState(true)
    
    try {
      const result = await this.submit(formData)
      
      if (result.success) {
        this.onSuccess(result)
      } else {
        this.onError(result.error || '提交失敗，請稍後再試')
      }
    } catch (error) {
      this.onError('網路錯誤，請檢查連線後再試')
    } finally {
      this.setLoadingState(false)
    }
  }
  
  displayErrors(errors) {
    // Clear previous errors
    this.clearErrors()
    
    for (const [field, message] of Object.entries(errors)) {
      const input = this.form.elements[field]
      const errorElement = document.getElementById(`${field}-error`)
      
      if (input) {
        input.classList.add('border-red-500')
        input.setAttribute('aria-invalid', 'true')
      }
      
      if (errorElement) {
        errorElement.textContent = message
        errorElement.classList.remove('hidden')
      }
    }
  }
  
  clearErrors() {
    const inputs = this.form.querySelectorAll('input, textarea, select')
    inputs.forEach(input => {
      input.classList.remove('border-red-500')
      input.removeAttribute('aria-invalid')
    })
    
    const errorElements = this.form.querySelectorAll('[id$="-error"]')
    errorElements.forEach(element => {
      element.textContent = ''
      element.classList.add('hidden')
    })
  }
  
  setLoadingState(loading) {
    this.isSubmitting = loading
    const submitButton = this.form.querySelector('button[type="submit"]')
    const submitText = submitButton?.querySelector('#' + this.getSubmitTextId())
    const loadingSpinner = submitButton?.querySelector('#' + this.getLoadingId())
    
    if (submitButton) {
      submitButton.disabled = loading
    }
    
    if (submitText && loadingSpinner) {
      if (loading) {
        submitText.classList.add('hidden')
        loadingSpinner.classList.remove('hidden')
      } else {
        submitText.classList.remove('hidden')
        loadingSpinner.classList.add('hidden')
      }
    }
  }
  
  onSuccess(result) {
    this.showToast('提交成功！', 'success')
    this.form.reset()
  }
  
  onError(message) {
    this.showToast(message, 'error')
  }
  
  showToast(message, type = 'info') {
    const toast = document.createElement('div')
    toast.className = `toast toast-${type} animate-slide-up`
    toast.textContent = message
    toast.setAttribute('role', 'alert')
    
    const container = document.getElementById('toast-container')
    if (container) {
      container.appendChild(toast)
      
      // Auto remove after 3 seconds
      setTimeout(() => {
        toast.classList.add('opacity-0', 'translate-x-full')
        setTimeout(() => {
          if (toast.parentNode) {
            toast.parentNode.removeChild(toast)
          }
        }, 300)
      }, 3000)
    }
  }
  
  // Abstract methods to be implemented by subclasses
  validate(data) {
    throw new Error('validate method must be implemented')
  }
  
  async submit(data) {
    throw new Error('submit method must be implemented')
  }
  
  getSubmitTextId() {
    throw new Error('getSubmitTextId method must be implemented')
  }
  
  getLoadingId() {
    throw new Error('getLoadingId method must be implemented')
  }
}

export class WaitlistForm extends BaseForm {
  validate(data) {
    const errors = {}
    
    if (!validateRequired(data.email)) {
      errors.email = 'Email 為必填欄位'
    } else if (!validateEmail(data.email)) {
      errors.email = '請輸入有效的 Email 地址'
    }
    
    if (!validateRequired(data.csrf_token)) {
      errors.csrf_token = 'CSRF token 遺失'
    }
    
    if (data.background && !validateMaxLength(data.background, 500)) {
      errors.background = '背景介紹不能超過 500 個字元'
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    }
  }
  
  async submit(data) {
    const response = await fetch('/api/waitlist', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })
    
    const result = await response.json()
    
    if (!response.ok) {
      return {
        success: false,
        error: result.error || `請求失敗 (${response.status})`
      }
    }
    
    return { success: true, data: result }
  }
  
  getSubmitTextId() {
    return 'waitlist-submit-text'
  }
  
  getLoadingId() {
    return 'waitlist-loading'
  }
  
  onSuccess(result) {
    super.onSuccess(result)
    
    // Track conversion event
    if (typeof gtag !== 'undefined') {
      gtag('event', 'waitlist_signup', {
        event_category: 'engagement',
        event_label: 'waitlist_form'
      })
    }
  }
}

export class InterviewForm extends BaseForm {
  validate(data) {
    const errors = {}
    
    if (!validateRequired(data.name)) {
      errors.name = '姓名為必填欄位'
    }
    
    if (!validateRequired(data.email)) {
      errors.email = 'Email 為必填欄位'
    } else if (!validateEmail(data.email)) {
      errors.email = '請輸入有效的 Email 地址'
    }
    
    if (!validateRequired(data.story)) {
      errors.story = '請分享您的故事'
    } else if (!validateMaxLength(data.story, 500)) {
      errors.story = '故事內容不能超過 500 個字元'
    }
    
    if (!validateRequired(data.csrf_token)) {
      errors.csrf_token = 'CSRF token 遺失'
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    }
  }
  
  async submit(data) {
    const response = await fetch('/api/interview', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })
    
    const result = await response.json()
    
    if (!response.ok) {
      return {
        success: false,
        error: result.error || `請求失敗 (${response.status})`
      }
    }
    
    return { success: true, data: result }
  }
  
  getSubmitTextId() {
    return 'interview-submit-text'
  }
  
  getLoadingId() {
    return 'interview-loading'
  }
  
  onSuccess(result) {
    super.onSuccess(result)
    this.showToast('申請已提交！我們將在 3 個工作日內回覆您。', 'success')
    
    // Close modal if it exists
    const modal = document.getElementById('interview-modal')
    if (modal) {
      modal.classList.add('hidden')
    }
    
    // Track conversion event
    if (typeof gtag !== 'undefined') {
      gtag('event', 'interview_application', {
        event_category: 'engagement',
        event_label: 'interview_form'
      })
    }
  }
}